/* eslint-disable no-nested-ternary */
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditRatioForm } from 'components/EditRatioForm';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    DismantlingCoefficientTypeEnum,
    PriceListRatioRowCreateInput,
    WorkOrderChangePriceData,
    WorkOrderChangePriceItem,
    WorkOrderPriceData,
    WorkOrderPriceItem,
} from 'gql/graphql';
import { RatioTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useIsUniqueRatioWorkConditions } from 'pages/Project/UerProject/WorkOrder/hooks/useIsUniqueRatioWorkConditions';
import { coefficientsItems } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { requiredBaseRule } from 'utils/constants';
import { replacerCost } from 'utils/replacerCost';

import type React from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { useController, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { updateRatioToActKC2FromPriceList } from '../../WorkOrder/workOrderQueries';

import {
    gqlCreateRatioRow,
    updateRatioRowGql,
    updateRatioToWorkOrderFromPriceListGql,
} from './createOrEditRatioQueries';
import { useDefaultsForDismantlingRatio } from './hooks/useDefaultsForDismantlingRatio';
import { usePriceListRatioRow } from './hooks/usePriceListRatioRow';
import { usePriceListUerForConstraintRatio } from './hooks/usePriceListUerForConstraintRatio';

interface ICreateOrEditRatioProps {
    ratioType?: RatioTypeEnum;
    onRatioTypeChange?: (coefficientType: RatioTypeEnum) => void;
    onClose: () => void;
    onConfirm: (id: number) => void;
    uer?: WorkOrderPriceData | WorkOrderPriceItem | WorkOrderChangePriceData | WorkOrderChangePriceItem;
    workOrderStatus?: WorkOrderStatusEnum;
    coeffId?: number;
    setCheckedIds?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    groupType?: DismantlingCoefficientTypeEnum | null;
}

const modalTitle = 'Создание коэффициента';
const modalEditTitle = 'Редактирование нового коэффициента из ВОР';
const modalEditTitleNoData = 'Редактирование коэффициента';
const modalEditTitleCustom = 'Редактирование коэффициента, созданного вручную';
const workConditionsKey = 'workConditions';

const techPartValidator = {
    validate: (v: any[]) => (!v?.length ? requiredBaseRule.required : undefined),
};

const constraintCoefValidator = {
    validate: (v: any[]) => (!v ? requiredBaseRule.required : undefined),
};

export const CreateOrEditRatio: React.FC<ICreateOrEditRatioProps> = (props) => {
    const {
        ratioType: ratioTypeProp,
        onClose,
        onConfirm,
        uer: uerProp,
        workOrderStatus,
        coeffId: coeffIdProp,
        setCheckedIds,
        groupType,
    } = props;

    const { id: projectId, coeffId: coeffIdParam } = useParams();
    const { state: uerLocation }: any = useLocation();
    const { projectData } = useProjectData(projectId);
    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        clearErrors,
        setError,
        setValue,
        reset,
        formState: { isDirty, errors },
    } = useForm();

    const uer = uerProp ?? uerLocation;

    const ratioRowId = useMemo(
        () => (coeffIdProp || coeffIdParam ? Number(coeffIdProp ?? coeffIdParam) : null),
        [coeffIdParam, coeffIdProp],
    );

    const [selectedCoefficientType, setSelectedCoefficientType] = useState<{ id: RatioTypeEnum; label: string }>();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isOpenDialogCoefficient, setIsOpenDialogCoefficient] = useState(false);

    const workConditions: string = useWatch({ control, name: workConditionsKey });
    const [initialGroupValue, setInitialGroupValue] = useState<any>();

    const isConstraintRatio = selectedCoefficientType?.id === RatioTypeEnum.ConstraintCoef;
    const isDismantlingRatio = selectedCoefficientType?.id === RatioTypeEnum.DismantlingCoef;

    const { priceListRatioRowStatus, priceListRatioRowSetData, priceListRatioRowDataItem } = usePriceListRatioRow({
        ratioRowId,
        reset,
        setValue,
        setInitialGroupValue,
    });

    const { setRatioDefaultsDismantlingData, defaultGroupType } = useDefaultsForDismantlingRatio({
        ratioRowId,
        uerBaseRowId: uer?.entryId,
        contractId: projectData?.contractId,
        isDismantlingRatio,
        groupType,
        setValue,
    });

    const { priceListUerForConstraintRatioDataSetData } = usePriceListUerForConstraintRatio({
        ratioRowId,
        uerBaseRowId: uer?.entryId,
        contractId: projectData?.contractId,
        setValue,
        setInitialGroupValue,
        isConstraintRatio,
    });

    const ratioType = useMemo(
        () => (ratioRowId ? priceListRatioRowDataItem?.ratioType : ratioTypeProp),
        [ratioRowId, priceListRatioRowDataItem?.ratioType, ratioTypeProp],
    ) as RatioTypeEnum;

    const editingRatioStatus = useMemo(() => priceListRatioRowDataItem?.status, [priceListRatioRowDataItem]);

    const isCustom = useMemo(() => priceListRatioRowDataItem?.isCustom, [priceListRatioRowDataItem?.isCustom]);

    const title = useMemo(
        () =>
            ratioRowId
                ? priceListRatioRowStatus !== RequestStatus.SUCCESS
                    ? modalEditTitleNoData
                    : isCustom
                    ? modalEditTitleCustom
                    : modalEditTitle
                : modalTitle,
        [ratioRowId, isCustom, priceListRatioRowStatus],
    );

    const updaterCreateRatioRowHandler = useCallback(
        (depsVars) => {
            if (depsVars && setCheckedIds) {
                setCheckedIds((checkedIds) => ({ ...checkedIds, [depsVars.payload.uerCreateRatioRow.data?.id]: true }));
            }
        },
        [setCheckedIds],
    );

    const [createRatioRowData, createRatioRowStatus, , createRatioRowMakeRequest, createRatioRowSetData] = useRequest({
        requestId: 'createRatioRow',
        query: gqlCreateRatioRow,
        withEffect: false,
        dependenciesUpdater: updaterCreateRatioRowHandler,
    });

    const [updateRatioRowData, updateRatioRowStatus, , updateRatioRowMakeRequest, updateRatioRowSetData] = useRequest({
        requestId: 'updateRatioRow',
        query: updateRatioRowGql,
        withEffect: false,
    });

    const [
        updateRatioToWorkOrderFromPriceListData,
        updateRatioToWorkOrderFromPriceListStatus,
        ,
        updateRatioToWorkOrderFromPriceListMakeRequest,
        updateRatioToWorkOrderFromPriceListSetData,
    ] = useRequest({
        requestId: 'updateRatioToWorkOrderFromPriceList',
        query: updateRatioToWorkOrderFromPriceListGql,
        withEffect: false,
    });

    const [resetUpdateRatioToActKC2Data] = useResetStoreData({
        requestId: 'updateRatioToActKC2FromPriceList',
    });
    const updateRatioToActKC2FromPriceListSuccessHandler = useCallback(() => {
        resetUpdateRatioToActKC2Data(undefined);
        onConfirm(Math.random());
    }, [onConfirm, resetUpdateRatioToActKC2Data]);

    const [, , , updateRatioToActKC2FromPriceListRequest] = useRequest({
        requestId: 'updateRatioToActKC2FromPriceList',
        query: updateRatioToActKC2FromPriceList,
        withEffect: false,
        dependenciesUpdater: updateRatioToActKC2FromPriceListSuccessHandler,
    });

    const { result: uniqueResult, message: uniqueMessage } = useIsUniqueRatioWorkConditions({
        contractId: projectData?.contractId,
        ratioRowId,
        workConditions,
    });

    const isApplicationAreaDisabled = useMemo(
        () => (isConstraintRatio || isDismantlingRatio) && (!!ratioRowId || !!initialGroupValue),
        [ratioRowId, initialGroupValue, isConstraintRatio, isDismantlingRatio],
    );

    const applicationAreaTooltipText = useMemo(
        () =>
            isApplicationAreaDisabled && priceListRatioRowDataItem?.groupIsArtificial
                ? 'Для определения вида работ коэффициента выберите область применения у родительской расценки'
                : undefined,
        [isApplicationAreaDisabled, priceListRatioRowDataItem?.groupIsArtificial],
    );

    useEffect(() => {
        setValue('appliesToUerItems', uer ? [{ id: ratioRowId ? uer.id : uer.uerId, code: uer.code }] : []);
        setValue('appliesToUerIds', uer ? [ratioRowId ? uer.id : uer.uerId] : []);
    }, [ratioRowId, setValue, uer]);

    useEffect(() => {
        if (workConditions && !uniqueResult) {
            setError(workConditionsKey, {
                message: uniqueMessage as string,
            });
        } else {
            clearErrors(workConditionsKey);
        }
    }, [clearErrors, workConditions, setError, uniqueResult, uniqueMessage]);

    useEffect(() => {
        const itemToSet = coefficientsItems.find((item) => item.id === ratioType);

        if (itemToSet) {
            setSelectedCoefficientType(itemToSet);
        }
    }, [ratioType]);

    useEffect(() => {
        if (
            (createRatioRowData?.uerCreateRatioRow?.result && createRatioRowStatus === RequestStatus.SUCCESS) ||
            (updateRatioRowData?.uerUpdateRatioRow?.result && updateRatioRowStatus === RequestStatus.SUCCESS) ||
            (updateRatioToWorkOrderFromPriceListData?.uerUpdateRatioToWorkOrderFromPriceList?.result &&
                updateRatioToWorkOrderFromPriceListStatus === RequestStatus.SUCCESS)
        ) {
            dispatch(createRatioRowSetData(undefined));
            dispatch(updateRatioRowSetData(undefined));
            dispatch(updateRatioToWorkOrderFromPriceListSetData(undefined));
            dispatch(priceListRatioRowSetData(undefined));
            dispatch(priceListUerForConstraintRatioDataSetData(undefined));
            dispatch(setRatioDefaultsDismantlingData(undefined));

            onConfirm(createRatioRowData?.uerCreateRatioRow?.data?.id);
            setSelectedCoefficientType(undefined);
        }
    }, [
        createRatioRowData?.uerCreateRatioRow,
        updateRatioRowData?.uerUpdateRatioRow,
        dispatch,
        onConfirm,
        createRatioRowSetData,
        createRatioRowStatus,
        updateRatioRowStatus,
        updateRatioRowSetData,
        updateRatioToWorkOrderFromPriceListData?.uerUpdateRatioToWorkOrderFromPriceList,
        updateRatioToWorkOrderFromPriceListStatus,
        updateRatioToWorkOrderFromPriceListSetData,
        priceListRatioRowSetData,
        priceListUerForConstraintRatioDataSetData,
        setRatioDefaultsDismantlingData,
    ]);

    const appliesToUerIdsControl = useMemo(
        () => ({
            control,
            name: 'appliesToUerIds',
            rules: ratioType === RatioTypeEnum.TechPartCoef ? techPartValidator : undefined,
        }),
        [ratioType, control],
    );
    const ratioGroupIdControl = useMemo(
        () => ({
            control,
            name: 'ratioGroupId',
            rules: ratioType === RatioTypeEnum.ConstraintCoef ? constraintCoefValidator : undefined,
        }),
        [ratioType, control],
    );

    const groupTypeControl = useMemo(
        () => ({
            control,
            name: 'groupType',
            rules: ratioType === RatioTypeEnum.DismantlingCoef ? constraintCoefValidator : undefined,
        }),
        [control, ratioType],
    );

    const { field: appliesToUerIdsField } = useController(appliesToUerIdsControl);

    const checkedIdsPrices = useMemo(
        () => (appliesToUerIdsField.value?.length ? appliesToUerIdsField.value : []),
        [appliesToUerIdsField.value],
    );

    const onSaveConfirm = useCallback(
        (submitData: Record<string, any>) => {
            setIsSubmitted(true);
            const input: PriceListRatioRowCreateInput = {
                kem: replacerCost(submitData.kem),
                kmat: replacerCost(submitData.kmat),
                knr: replacerCost(submitData.knr),
                kozp: replacerCost(submitData.kozp),
                ksp: replacerCost(submitData.ksp),
                ktz: replacerCost(submitData.ktz),
                ktzzpm: replacerCost(submitData.ktzzpm),
                contractId: projectData?.contractId,
                ratioType,
                justification: submitData.justification,
                workConditions: submitData.workConditions,
                appliesToUerRowIds: submitData.appliesToUerIds,
                ratioGroupId: submitData.ratioGroupId,
                groupType: submitData.groupType,
            };

            if (ratioRowId) {
                if (uer?.priceListId) {
                    if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                        dispatch(
                            updateRatioToActKC2FromPriceListRequest({
                                workOrderPriceId: uer?.id,
                                input: { ...input, ratioRowId },
                            }),
                        );
                    } else {
                        dispatch(
                            updateRatioToWorkOrderFromPriceListMakeRequest({
                                input: { ...input, ratioRowId },
                                workOrderPriceId: uer?.id,
                            }),
                        );
                    }
                } else {
                    dispatch(updateRatioRowMakeRequest({ input: { ...input, ratioRowId } }));
                }
            } else {
                dispatch(
                    createRatioRowMakeRequest({
                        input: {
                            ...input,
                            targetUerId: uer?.uerId,
                            appliesToUerRowIds: [...(uer?.uerId != null ? [uer.uerId] : []), ...checkedIdsPrices],
                        },
                    }),
                );
            }
        },
        [
            projectData?.contractId,
            ratioType,
            ratioRowId,
            uer,
            workOrderStatus,
            dispatch,
            updateRatioToActKC2FromPriceListRequest,
            updateRatioToWorkOrderFromPriceListMakeRequest,
            updateRatioRowMakeRequest,
            createRatioRowMakeRequest,
            checkedIdsPrices,
        ],
    );

    const onCloseExactly = useCallback(() => {
        dispatch(createRatioRowSetData(undefined));
        dispatch(updateRatioRowSetData(undefined));
        dispatch(updateRatioToWorkOrderFromPriceListSetData(undefined));
        dispatch(priceListRatioRowSetData(undefined));
        dispatch(priceListUerForConstraintRatioDataSetData(undefined));
        dispatch(setRatioDefaultsDismantlingData(undefined));
        setSelectedCoefficientType(undefined);

        onClose?.();
    }, [
        dispatch,
        createRatioRowSetData,
        updateRatioRowSetData,
        updateRatioToWorkOrderFromPriceListSetData,
        priceListRatioRowSetData,
        priceListUerForConstraintRatioDataSetData,
        setRatioDefaultsDismantlingData,
        onClose,
    ]);

    const onCloseDialog = useCallback(() => {
        if (!isSubmitted) {
            if (isDirty) {
                setIsOpenDialogCoefficient(true);
            } else {
                onCloseExactly();
            }
        } else {
            onCloseExactly();
        }
    }, [isDirty, isSubmitted, onCloseExactly]);

    const handleSubmitDialogStatus = useCallback(() => {
        setIsOpenDialogCoefficient(false);
        onCloseDialog();
        onCloseExactly();
    }, [onCloseDialog, onCloseExactly]);

    const handleCancelDialogStatus = useCallback(() => {
        setIsOpenDialogCoefficient(false);
    }, []);

    const applicationSelectorDisabledIds = useMemo(() => {
        const usedIds = (priceListRatioRowDataItem?.appliesTo || [])
            .filter((item) => item.isUsedLink)
            .map((item) => item.appliesToId);

        return !isConstraintRatio && !isDismantlingRatio && uer
            ? [ratioRowId ? uer?.id : uer?.uerId, ...usedIds]
            : usedIds;
    }, [isConstraintRatio, isDismantlingRatio, priceListRatioRowDataItem?.appliesTo, ratioRowId, uer]);

    const btnConfirmDisabled = useMemo(
        () => !isDirty || createRatioRowStatus === RequestStatus.FETCHING || !!Object.keys(errors || {}).length,
        [createRatioRowStatus, errors, isDirty],
    );

    useEffect(() => {
        if (!ratioRowId) {
            ['kem', 'kmat', 'knr', 'kozp', 'ksp', 'ktz', 'ktzzpm'].forEach((v) => setValue(v, 1));
        }
    }, [ratioRowId, setValue]);

    useEffect(() => {
        if (groupType) {
            setValue('groupType', groupType);
        }
    }, [groupType, setValue]);

    const currentUerId = useMemo(() => {
        if (coeffIdProp) {
            return uer?.uerId;
        }

        return ratioRowId ? uer?.id : uer?.uerId;
    }, [coeffIdProp, ratioRowId, uer?.id, uer?.uerId]);

    return (
        <>
            <ModalDialog
                title={title}
                isOpen
                hasOverlay
                onClose={onCloseDialog}
                onConfirm={handleSubmit(onSaveConfirm)}
                btnConfirmLabel={ratioRowId ? 'Сохранить' : 'Создать'}
                btnCancelLabel="Отменить"
                withTitleCloseButton
                style={{ width: 600 }}
                btnConfirmDisabled={btnConfirmDisabled}
                isOnClickOutsideDisable
            >
                <EditRatioForm
                    isDataFetching={priceListRatioRowStatus === RequestStatus.FETCHING}
                    ratioId={ratioRowId}
                    control={control}
                    isCustom={isCustom}
                    selectedCoefficientType={selectedCoefficientType}
                    ratioType={ratioType}
                    appliesToUerIdsControl={appliesToUerIdsControl}
                    ratioGroupIdControl={ratioGroupIdControl}
                    isApplicationAreaDisabled={isApplicationAreaDisabled}
                    applicationSelectorDisabledIds={applicationSelectorDisabledIds}
                    applicationAreaTooltipText={applicationAreaTooltipText}
                    groupTypeControl={groupTypeControl}
                    contractId={projectData?.contractId}
                    uerId={currentUerId}
                    groupType={coeffIdProp ? priceListRatioRowDataItem?.groupType : groupType ?? defaultGroupType}
                    editingRatioStatus={editingRatioStatus}
                />
            </ModalDialog>

            <ConfirmationDialog
                textHeader="ОТМЕНА ДЕЙСТВИЙ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenDialogCoefficient}
                onSubmit={handleSubmitDialogStatus}
                onClose={handleCancelDialogStatus}
            />
        </>
    );
};

CreateOrEditRatio.displayName = 'CreateOrEditRatio';
